import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { CreatableSelect } from '@dealstack/frontend/shared/components/forms/CreatableSelect/CreatableSelect';
import { memo, useContext, useEffect, useState } from 'react';
import { Control, Controller, useFormContext } from 'react-hook-form';

import { CreateNewEntity } from './CreateNewEntity/CreateNewEntity';

interface Props {
	name: string;
	control: Control;
	label: string;
	placeholder: string;
	setDisableButton?: (disable: boolean) => void;
	dataTestId?: string;
}

const EntitySelect = ({
	setDisableButton,
	name,
	control,
	label,
	placeholder,
	dataTestId,
}: Props) => {
	const formContext = useFormContext();
	const { personsSelectOptions, refetchPersons, dealId } = useContext(CapManageControllerContext);
	const [createdEntityId, setCreatedEntityId] = useState<string>();
	const [showCreateNewEntityBox, setShowCreateNewEntityBox] = useState<boolean>(false);

	const onCreated = async (createdEntityId: string) => {
		await refetchPersons();
		setCreatedEntityId(createdEntityId);
		setDisableButton && setDisableButton(false);
		setShowCreateNewEntityBox(false);
	};

	useEffect(() => {
		if (!createdEntityId) {
			return;
		}
		const createdEntityInOptions = personsSelectOptions.find(
			(option) => option.value === createdEntityId
		);
		if (!createdEntityInOptions) {
			return;
		}

		formContext.setValue(name, createdEntityId);
		setCreatedEntityId(undefined);
	}, [createdEntityId, personsSelectOptions.length]);

	return (
		<>
			{!showCreateNewEntityBox && (
				<Controller
					name={name}
					control={control}
					render={({ field }) => (
						<CreatableSelect
							value={field.value ?? null}
							onChange={field.onChange}
							onBlur={field.onBlur}
							data={personsSelectOptions}
							label={label}
							placeholder={placeholder}
							size="md"
							clearable
							getCreateLabel={() => 'Create new'}
							shouldCreate={() => true}
							onCreate={() => {
								setShowCreateNewEntityBox(true);
								setDisableButton && setDisableButton(true);
							}}
							data-testid={dataTestId}
						/>
					)}
				/>
			)}

			{showCreateNewEntityBox && (
				<CreateNewEntity
					onCreated={onCreated}
					dealId={dealId}
					onCancel={() => {
						setShowCreateNewEntityBox(false);
						setDisableButton && setDisableButton(false);
					}}
				/>
			)}
		</>
	);
};
const _EntitySelectExport = memo(EntitySelect);

export { _EntitySelectExport as EntitySelect };
